import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, Observable, of, Subject, tap } from 'rxjs';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { BookingService, BranchService, GenderPipe, trackByIndex, User, UserService, UserSourceService } from '@beautyapp/core';
import { switchMap, takeUntil } from 'rxjs/operators';
import { NzDrawerService } from 'ng-zorro-antd/drawer';
import { NzModalService } from 'ng-zorro-antd/modal';
import { UserViewComponent } from '../user-view/user-view.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { UserDeleteAlertComponent } from '../user-delete-alert/user-delete-alert.component';
import { UserAddEditComponent } from '@beautyapp/shared';
import { AsyncPipe, DatePipe, DecimalPipe } from '@angular/common';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { InputComponent, SelectComponent } from '@beautyapp/form';
import { NzTooltipDirective } from 'ng-zorro-antd/tooltip';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NgxPermissionsModule } from 'ngx-permissions';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzSpaceModule } from 'ng-zorro-antd/space';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { RouterLink } from '@angular/router';

@Component({
  selector: 'beautyapp-users-list',
  templateUrl: './users-list.component.html',
  imports: [
    TranslatePipe,
    GenderPipe,
    DatePipe,
    DecimalPipe,
    AsyncPipe,
    NzGridModule,
    NzFormModule,
    FormsModule,
    ReactiveFormsModule,
    NzButtonModule,
    NzCheckboxModule,
    InputComponent,
    SelectComponent,
    NzTooltipDirective,
    NzIconModule,
    NgxPermissionsModule,
    NzTableModule,
    NzSpaceModule,
    NzTagModule,
    NzPopconfirmModule,
    RouterLink
  ],
  styleUrls: ['./users-list.component.scss']
})
export class UsersListComponent implements OnInit, OnDestroy {
  private userService = inject(UserService);
  private bookingService = inject(BookingService);
  private branchService = inject(BranchService);
  private userSourceService = inject(UserSourceService);
  private modalService = inject(NzModalService);
  private drawerService = inject(NzDrawerService);
  private fb = inject(UntypedFormBuilder);
  private translateService = inject(TranslateService);

  userSources = toSignal(this.userSourceService.dropdown(), { initialValue: [] });

  public userTypes = ['Admin', 'User', 'Other'];
  loading = false;
  loadData: User[] = [];
  total = 1;
  paginatorPageSize = 10;
  paginatorPageIndex = 1;
  searchForm: UntypedFormGroup;
  searchValue?: string;
  branchId?: string;
  sub$: Subject<any> = new Subject<any>();
  branches$!: Observable<any>;
  trackByIndex = trackByIndex;

  constructor() {
    this.searchForm = this.fb.group({
      search: null,
      branch: null,
      userSourceId: null,
      withBalance: false,
      withMinutes: false
    });
  }

  ngOnInit() {

    this.getUsers();
    this.getBranches();
  }

  private getUsers() {
    this.loading = true;
    const params = {
      page: this.paginatorPageIndex,
      pageSize: this.paginatorPageSize,
      q: this.searchValue,
      branchId: this.branchId,
      userSourceId: this.searchForm.value.userSourceId,
      withBalance: this.searchForm.value.withBalance,
      withMinutes: this.searchForm.value.withMinutes
    };
    this.userService
      .getAll(params)
      .pipe(takeUntil(this.sub$))
      .subscribe((res) => {
        this.loading = false;
        this.loadData = res.data;
        this.total = res.meta.itemCount;
      });
  }

  getBranches() {
    this.branches$ = this.branchService.getMyBranches();
  }

  paginatorChange(pageEvent: any) {
    this.paginatorPageSize = pageEvent.pageSize;
    this.paginatorPageIndex = pageEvent.pageIndex;
    this.getUsers();
  }

  checkUserBookings(userId: string) {
    return this.bookingService.checkUserBookings(userId);
  }

  delete(item: any) {
    if (item.id) {
      this.checkUserBookings(item.id)
        .pipe(
          takeUntil(this.sub$),
          switchMap(
            (res) => {

              if (res.length > 0) {
                return this.modalService.create({
                  nzTitle: 'User Delete',
                  nzContent: UserDeleteAlertComponent,
                  nzData: {
                    bookings: res
                  },
                  nzWidth: '800px'
                }).afterClose;
              }
              return of(true)
            }
          ),
          // Filter out non-confirmations (when user clicks Cancel or closes modal)
          filter((confirmed) => confirmed === true),
          // Proceed with deletion if confirmed
          switchMap(() => this.userService.delete(item.id)),
          // Optionally handle success with additional operations
          tap(() => {
            // Handle successful deletion operations here
            this.getUsers(); // Refresh the users list
            // You could also show a success notification here
          })
        )
        .subscribe();

      // this.userService
      //   .delete(item.id)
      //   .pipe(takeUntil(this.sub$))
      //   .subscribe((res) => {
      //     this.getUsers();
      //   });
    }
  }

  addOrEdit(event?: any) {
    this.modalService.create({
      nzTitle: this.translateService.instant('users.addUser/edit'),
      nzContent: UserAddEditComponent,
      nzWidth: '800px',
      nzData: {
        user: event
      },
      nzOnOk: () => {
        this.getUsers();
      }
    });
  }

  view(event: any) {
    let title = `${event.firstname} ${event.lastname || ''}`;
    if (event.cardNo) {
      title += ` - # ${event.cardNo}`;
    }
    const drawerRef = this.drawerService.create<
      UserViewComponent,
      { data: string },
      string
    >({
      nzTitle: title,
      nzContent: UserViewComponent,
      nzWrapClassName: 'user-drawer',
      nzBodyStyle: {
        padding: '0 15px 15px 15px'
      },
      nzData: {
        data: event.id
      }
    });

    drawerRef.afterClose.pipe(takeUntil(this.sub$)).subscribe((data) => {

      if (typeof data === 'string') {

      }
      this.getUsers();
    });
    // const overlayRef = this.overlay.open(CustomerViewComponent, {
    //   data: event.id,
    //   fullHeight: true,
    //   positionVertical: {placement: 'bottom'},
    //   positionHorizontal: {placement: 'right'}
    // });
    //
    // overlayRef
    //   .afterClose()
    //   .subscribe(response => {
    //     console.log(response);
    //     if (response) {
    //
    //     }
    //   });
  }

  search() {
    if (this.searchForm.value) {
      this.paginatorPageIndex = 1;
      this.searchValue = this.searchForm.value.search || 'undefined';
      this.branchId = this.searchForm.value.branch || 'undefined';
      this.getUsers();
    }
  }

  export() {
    return this.userService.export('Customers.xlsx');
  }

  branchSelected($event: any) {
    this.branchId = $event;
    this.getUsers();
  }

  clearSearch() {
    this.searchForm.patchValue({
      search: null,
      branch: null,
      userSourceId: null,
      withBalance: false,
      withMinutes: false
    });
    this.search();
    this.searchValue = undefined;
    this.branchId = undefined;
  }

  ngOnDestroy(): void {
    this.sub$.next(null);
    this.sub$.complete();
  }

  userVerify(row: User) {
    if (row.isVerified) {
      return;
    }
  }
}
